// =============================================================================
// RideDirections - "Directions to pickup" / "Directions to the clinic" for a
// care team driver (Oct 2026, 20270106100000_ride_driver_directions).
//
// Shows the pickup address (the transportation profile's pickup address, else
// the patient's home) and opens the phone's maps app with driving directions:
// to the pickup before Picked up, to the clinic after. The addresses come from
// ride_driver_directions (the driver or a supervisor only; audited). Used on
// the driver's ride panel (Care Schedule visit, Today in the field) and the
// Ride alerts card.
// =============================================================================
import { useEffect, useState } from "react";
import { C } from "../../lib/tokens";
import { fetchRideDirections } from "../../lib/ridesApi";
import { mapsUrl, rideNeedsList } from "../../lib/ridesCore";

const linkStyle = (primary) => ({
  display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34, padding: "0 12px", borderRadius: 6,
  fontSize: 13, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap",
  border: primary ? "none" : "1px solid " + C.borderMid,
  background: primary ? "var(--pos-btn-primary-bg)" : C.bgPrimary,
  color: primary ? "var(--brand-contrast, #fff)" : "var(--brand-text)",
});

const Pin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" />
  </svg>
);

// rideId: the appointment_rides id. pickedUp: show the clinic leg instead.
// home: the ride home after the visit (the pickup address, labeled Home).
// showAddress: print the pickup address above the button.
// The ride needs (wheelchair, someone riding along, the pickup note) show on
// the way to the pickup.
export default function RideDirections({ rideId, pickedUp = false, home = false, showAddress = true, primary = true }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    if (!rideId) return undefined;
    setD(null); setErr(false);
    fetchRideDirections(rideId).then((x) => { if (live) setD(x); })
      // supabase-best-effort: the ride still shows; only the directions are missing.
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [rideId]);

  if (!rideId) return null;
  // Not this person's ride (and not a supervisor), or the read failed: no link.
  if (err || !d) return null;
  const toClinic = pickedUp && !home;
  const to = toClinic ? d.clinic_address : d.pickup_address;
  const url = mapsUrl(to);
  // Rides block 5: mobility, oxygen, service animal, car seat, bariatric,
  // door-to-door and interpreter come from ride_driver_directions.
  const needs = rideNeedsList(d).map((x, i) => (i === 0 ? x.charAt(0).toUpperCase() + x.slice(1) : x)).concat(d.note ? [d.note] : []);
  return (
    <div data-testid="ride-directions" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
      {showAddress && (
        <div style={{ fontSize: 12, color: C.textSecondary, flex: "1 1 180px", minWidth: 0, overflowWrap: "anywhere" }}>
          <span style={{ fontWeight: 600, color: C.textPrimary }}>{toClinic ? "Clinic: " : home ? "Home: " : "Pickup: "}</span>
          {to || (toClinic ? "No clinic address on file." : "No pickup address on file. Add one on the patient's transportation profile.")}
        </div>
      )}
      {!toClinic && needs.length > 0 && (
        <div data-testid="ride-directions-needs" style={{ fontSize: 12, color: C.textSecondary, flexBasis: "100%", overflowWrap: "anywhere" }}>
          <span style={{ fontWeight: 600, color: C.textPrimary }}>Needs: </span>{needs.join(" - ")}
        </div>
      )}
      {url && (
        <a data-testid="ride-directions-link" href={url} target="_blank" rel="noopener noreferrer" style={linkStyle(primary)}>
          <Pin />{toClinic ? "Directions to the clinic" : home ? "Directions home" : "Directions to pickup"}
        </a>
      )}
    </div>
  );
}
